// src/components/Contact/Contact.jsx
import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "@emailjs/browser";
import ThemedButton from "../ThemeButton/ThemedButton";
import {
  t as translate,
  normalizeLang,
  getLangFromPathname,
} from "../../i18n";
import { deepMerge, useSiteContent } from "../../content/SiteContentContext";

// ------------------------------
// GA4 helper
// ------------------------------
const fireEvent = (name, params = {}) => {
  if (window.gtag) window.gtag("event", name, params);
};

// CRA (react-scripts) ei tue Vite-tyylistä import.meta.env -syntaksia.
// Voit syöttää asetukset joko:
// 1) runtime-configiin (window.ARTTU_CONFIG / window.RUNTIME_CONFIG)
// 2) .env.localiin (REACT_APP_* -muuttujat)
const cfg = window.ARTTU_CONFIG || window.RUNTIME_CONFIG || {};

const Contact = ({ lang: langProp } = {}) => {
  const siteContent = useSiteContent();
  const form = useRef(null);
  const recaptchaRef = useRef(null);
  const successModalCloseRef = useRef(null);
  const successModalTimerRef = useRef(null);

  // reCAPTCHA v2 checkbox on kiinteäleveyksinen (~304px), joten skaalataan mobiilissa
  const recaptchaWrapRef = useRef(null);
  const RECAPTCHA_WIDTH = 304;
  const RECAPTCHA_HEIGHT = 78;
  const [recaptchaScale, setRecaptchaScale] = useState(1);

  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({ type: "idle", msg: "" }); // idle | error

  // Onnistunut lähetys näytetään omassa animoidussa modalissa.
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalClosing, setSuccessModalClosing] = useState(false);

  // “Form start” - lähetetään vain kerran kun user alkaa täyttää
  const [formStarted, setFormStarted] = useState(false);

  // Home välittää normaalisti kielen propina.
  // Jos komponenttia käytetään ilman sitä, päätellään kieli URL:sta.
  const pathname =
    typeof window !== "undefined"
      ? window.location.pathname
      : "/";

  const lang =
    typeof langProp === "string" && langProp
      ? normalizeLang(langProp)
      : getLangFromPathname(pathname);

  // Käytetään samaa yhteistä i18n-logiikkaa kuin muualla etusivulla.
  // fallbackValue estää käännösavaimen näkymisen ruudulla, jos avain puuttuu.
  const t = (key, vars, fallbackValue) => {
    const result = translate(key, lang, vars);

    if (result === key && typeof fallbackValue === "string") {
      return fallbackValue;
    }

    return result;
  };

  // Modalin fallback-tekstit pidetään tässä, jotta FI/EN/ES toimii heti
  // ilman että i18n-resurssit on pakko päivittää samalla muutoksella.
  const successModalText = useMemo(() => {
    const copy = {
      fi: {
        eyebrow: "VIESTI LÄHETETTY",
        title: "Jes — viestisi tuli perille!",
        body: "Kiitos yhteydenotosta. Käyn viestisi läpi ja palaan asiaan mahdollisimman pian.",
        first: "Viesti vastaanotettu",
        second: "Saat vastauksen sähköpostiin",
        close: "Selvä, kiitos",
        closeAria: "Sulje onnistumisikkuna",
      },
      en: {
        eyebrow: "MESSAGE SENT",
        title: "Nice — your message is in!",
        body: "Thanks for getting in touch. I’ll review your message and get back to you as soon as possible.",
        first: "Message received",
        second: "You’ll get a reply by email",
        close: "Got it, thanks",
        closeAria: "Close success dialog",
      },
      es: {
        eyebrow: "MENSAJE ENVIADO",
        title: "¡Genial — tu mensaje ha llegado!",
        body: "Gracias por contactar. Revisaré tu mensaje y te responderé lo antes posible.",
        first: "Mensaje recibido",
        second: "Recibirás la respuesta por correo",
        close: "Perfecto, gracias",
        closeAria: "Cerrar ventana de confirmación",
      },
    };

    return deepMerge(
      copy[lang] || copy.fi,
      siteContent?.pages?.contact?.[lang]?.successModal || {}
    );
  }, [lang, siteContent]);

  // Lomakkeen uudet UI-tekstit pidetään lokaalisti, jotta uusi ulkoasu toimii
  // heti kaikilla nykyisillä kielillä ilman i18n-tiedostojen pakollista päivitystä.
  const formUiText = useMemo(() => {
    const copy = {
      fi: {
        eyebrow: "KERRO PROJEKTISTA",
        hint: "Kolme kenttää · noin minuutti",
        secureTitle: "Roskapostisuoja",
        securePending: "Tarkistus",
        secureVerified: "Vahvistettu",
        privacy: "Ei sitoumuksia — vastaan ensin toteutuksesta ja hinta-arviosta.",
      },
      en: {
        eyebrow: "TELL ME ABOUT YOUR PROJECT",
        hint: "Three fields · about a minute",
        secureTitle: "Spam protection",
        securePending: "Check",
        secureVerified: "Verified",
        privacy: "No commitment — I’ll first reply with an implementation and price estimate.",
      },
      es: {
        eyebrow: "CUÉNTAME TU PROYECTO",
        hint: "Tres campos · aproximadamente un minuto",
        secureTitle: "Protección antispam",
        securePending: "Comprobar",
        secureVerified: "Verificado",
        privacy: "Sin compromiso — primero te responderé con una propuesta y una estimación de precio.",
      },
    };

    return deepMerge(
      copy[lang] || copy.fi,
      siteContent?.pages?.contact?.[lang]?.formUi || {}
    );
  }, [lang, siteContent]);

  const closeSuccessModal = useCallback(() => {
    if (!showSuccessModal || successModalClosing) return;

    setSuccessModalClosing(true);

    if (successModalTimerRef.current) {
      window.clearTimeout(successModalTimerRef.current);
    }

    successModalTimerRef.current = window.setTimeout(() => {
      setShowSuccessModal(false);
      setSuccessModalClosing(false);
      successModalTimerRef.current = null;
    }, 220);
  }, [showSuccessModal, successModalClosing]);

  // Kun modal on auki: lukitaan taustan scrollaus, fokusoidaan sulkupainike
  // ja sallitaan sulkeminen ESC-näppäimellä.
  useEffect(() => {
    if (!showSuccessModal || typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusTimer = window.setTimeout(() => {
      successModalCloseRef.current?.focus();
    }, 80);

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSuccessModal();
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [showSuccessModal, closeSuccessModal]);

  // Puretaan mahdollinen sulkeutumistimeri komponentin poistuessa.
  useEffect(() => {
    return () => {
      if (successModalTimerRef.current) {
        window.clearTimeout(successModalTimerRef.current);
      }
    };
  }, []);

  // Responsiivinen reCAPTCHA-skaalaus (mobiili)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const el = recaptchaWrapRef.current;
    if (!el) return;

    const updateScale = () => {
      const available = el.clientWidth || RECAPTCHA_WIDTH;
      const nextScale = Math.min(1, available / RECAPTCHA_WIDTH);

      // Vältetään turhat rerenderit pienen desimaalivärinän takia
      setRecaptchaScale((prev) =>
        Math.abs(prev - nextScale) < 0.01 ? prev : nextScale
      );
    };

    updateScale();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateScale);
      ro.observe(el);
    } else {
      window.addEventListener("resize", updateScale);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", updateScale);
    };
  }, []);


  // EmailJS asetukset (CRA):
  // .env.local:
  // REACT_APP_EMAILJS_PUBLIC_KEY=...
  // REACT_APP_EMAILJS_SERVICE_ID=...
  // REACT_APP_EMAILJS_TEMPLATE_ID=...
  const EMAILJS_PUBLIC_KEY =
    cfg.EMAILJS_PUBLIC_KEY || process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "Z2CRLs0fQ7Vv-UmBv";
  const EMAILJS_SERVICE_ID =
    cfg.EMAILJS_SERVICE_ID || process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_h4xqafa";
  const EMAILJS_TEMPLATE_ID =
    cfg.EMAILJS_TEMPLATE_ID || process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_ubsiz86";

  // Initit kerran
  useEffect(() => {
    // EmailJS init (public key)
    // HUOM: älä käytä private keytä frontissa
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, [EMAILJS_PUBLIC_KEY]);

  // GA4 view (voi vaihtua kielen mukana)
  useEffect(() => {
    fireEvent("contact_view", {
      page_path: window.location?.pathname || "",
      page_title: document.title || "",
      lang,
    });
  }, [lang]);

  const markFormStarted = useCallback(() => {
    if (!formStarted) {
      setFormStarted(true);
      fireEvent("contact_form_start", {
        page_path: window.location?.pathname || "",
        lang,
      });
    }
  }, [formStarted, lang]);

  const onCaptchaChange = (token) => {
    const ok = Boolean(token);
    setCaptchaVerified(ok);

    fireEvent(ok ? "contact_captcha_verified" : "contact_captcha_cleared", {
      page_path: window.location?.pathname || "",
      lang,
    });
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    markFormStarted();

    fireEvent("contact_submit_attempt", {
      captcha_ok: captchaVerified,
      page_path: window.location?.pathname || "",
      lang,
    });

    if (!captchaVerified) {
      setStatus({
        type: "error",
        msg: t(
          "contact.form.status.captchaRequired",
          null,
          "Varmista ensin CAPTCHA (en halua spämmibotteja elämääni)."
        ),
      });
      return;
    }

    if (!form.current) return;

    try {
      setIsSending(true);
      setStatus({ type: "idle", msg: "" });

      await emailjs.sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        form.current,
        EMAILJS_PUBLIC_KEY
      );

      // Nollataan lomake ja CAPTCHA vasta onnistuneen lähetyksen jälkeen.
      form.current.reset();
      recaptchaRef.current?.reset();
      setCaptchaVerified(false);
      setFormStarted(false);
      setStatus({ type: "idle", msg: "" });

      // GA4-eventti pidetään täsmälleen samana kuin ennen.
      fireEvent("contact_submit_success", {
        page_path: window.location?.pathname || "",
        template_id: EMAILJS_TEMPLATE_ID,
        service_id: EMAILJS_SERVICE_ID,
        lang,
      });

      // Onnistuminen ei jää enää pieneksi inline-tekstiksi, vaan avaa oman modalin.
      setSuccessModalClosing(false);
      setShowSuccessModal(true);
    } catch (err) {
      setStatus({
        type: "error",
        msg: t(
          "contact.form.status.error",
          null,
          "Lähetys epäonnistui. Kokeile hetken päästä uudelleen."
        ),
      });

      fireEvent("contact_submit_error", {
        page_path: window.location?.pathname || "",
        template_id: EMAILJS_TEMPLATE_ID,
        error_name: err?.name || "unknown",
        lang,
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="relative">
      <section
        id="contact"
        className="mx-auto w-full scroll-mt-24 px-2 pb-24 sm:px-4 md:px-6 lg:px-12 xl:px-16"
        style={{ overflowAnchor: "none" }}
        aria-label={t("contact.title", null, "Ota yhteyttä")}
      >
        <div className="mx-auto w-full max-w-6xl">
          <div className="relative overflow-hidden rounded-[2rem] border border-black/10 bg-white/70 px-3 py-8 shadow-[0_20px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.04] dark:shadow-[0_20px_80px_rgba(0,0,0,0.25)] sm:px-5 md:px-8 md:py-10 lg:px-10">
            {/* Hienovaraiset taustahehkut pitävät tyylin samana muiden osioiden kanssa. */}
            <div
              className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full bg-lime-400/15 opacity-40 blur-3xl"
              aria-hidden="true"
            />
            <div
              className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-sky-400/15 opacity-30 blur-3xl"
              aria-hidden="true"
            />

            <div className="relative z-10 mb-10 text-center">
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-black/50 dark:text-white/45">
                {formUiText.eyebrow}
              </p>

              <h2 className="mt-3 text-3xl font-bold tracking-tight text-black dark:text-white md:text-4xl">
                {t("contact.title", null, "Ota yhteyttä")}
              </h2>

              <p className="mx-auto mt-4 max-w-2xl text-base font-light leading-relaxed text-black/75 dark:text-white/75 md:text-lg">
                {t(
                  "contact.lead",
                  null,
                  "Kerro lyhyesti mitä olet tekemässä — vastaan nopeasti ja voidaan sopia vaikka 30 min Teams-sparri."
                )}
              </p>

              {/* pieni luottamusrivi */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2 text-xs md:text-sm">
                <span className="rounded-full border border-black/10 bg-white/80 px-3 py-1 text-black/70 dark:border-white/10 dark:bg-white/5 dark:text-white/75">
                  {t("contact.trust.fastReply", null, "✅ Nopea vastaus")}
                </span>
                <span className="rounded-full border border-black/10 bg-white/80 px-3 py-1 text-black/70 dark:border-white/10 dark:bg-white/5 dark:text-white/75">
                  {t("contact.trust.noCommitment", null, "✅ Ei sitoumusta")}
                </span>
                <span className="rounded-full border border-black/10 bg-white/80 px-3 py-1 text-black/70 dark:border-white/10 dark:bg-white/5 dark:text-white/75">
                  {t("contact.trust.nextStep", null, "✅ Selkeä seuraava askel")}
                </span>
              </div>
            </div>

            <div
              className="relative z-10 grid grid-cols-1 items-start gap-6 lg:grid-cols-2"
              style={{ overflowAnchor: "none" }}
            >
            {/* vasen: “pitch-kortti” */}
            <div
              className="rounded-2xl border border-black/10 bg-white/80 px-4 py-6 dark:border-white/10 dark:bg-white/5 sm:p-6 md:p-8"
            >
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t("contact.guide.title", null, "Mitä kannattaa kirjoittaa?")}
              </h3>

              <ul className="mt-4 space-y-3 text-gray-700 dark:text-gray-200 text-sm md:text-base">
                <li className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-500" aria-hidden="true" />
                  <span>
                    <span className="font-semibold">
                      {t("contact.guide.items.project.label", null, "Mikä projekti?")}
                    </span>{" "}
                    {t(
                      "contact.guide.items.project.text",
                      null,
                      "(sivusto, portfolio, verkkokauppa, SPA…)"
                    )}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-500" aria-hidden="true" />
                  <span>
                    <span className="font-semibold">
                      {t("contact.guide.items.goal.label", null, "Mikä tavoite?")}
                    </span>{" "}
                    {t(
                      "contact.guide.items.goal.text",
                      null,
                      "(liidejä, myyntiä, näkyvyyttä, automaatiota)"
                    )}
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-lime-500" aria-hidden="true" />
                  <span>
                    <span className="font-semibold">
                      {t(
                        "contact.guide.items.scheduleBudget.label",
                        null,
                        "Aikataulu & budjettihaarukka"
                      )}
                    </span>{" "}
                    {t("contact.guide.items.scheduleBudget.text", null, "(jos tiedossa)")}
                  </span>
                </li>
              </ul>

              <div className="mt-6 rounded-xl p-4 bg-lime-500/10 border border-lime-400/20">
                <p className="text-sm md:text-base text-gray-800 dark:text-gray-100">
                  {t(
                    "contact.guide.note",
                    null,
                    "Et saa pelkkää tarjousta — saat selkeän näkemyksen siitä, mitä sivulla kannattaa tehdä ensin, mitä mitata ja miten yhteydenottoja voidaan lisätä käytännössä."
                  )}
                </p>
              </div>
            </div>

            {/* oikea: animoitu premium-lomake */}
            <div className="contact-form-shell relative overflow-hidden rounded-2xl border border-black/10 bg-white/90 shadow-[0_14px_40px_rgba(0,0,0,0.08)] dark:border-lime-400/20 dark:bg-white/5 dark:shadow-[0_18px_50px_rgba(0,0,0,0.22)]">
              {/* Hillitty liikkuva valo — tumma kortti pysyy osana sivuston nykyistä ilmettä. */}
              <div className="contact-form-sheen pointer-events-none absolute inset-y-0 -left-1/2 w-1/3 rotate-12 bg-gradient-to-r from-transparent via-lime-300/10 to-transparent opacity-0 md:opacity-100" />
              <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-lime-400/10 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -left-16 h-52 w-52 rounded-full bg-blue-400/10 blur-3xl" />

              <div className="relative px-3 py-5 sm:p-6 md:p-7">
                <div className="mb-6 flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[11px] font-black tracking-[0.22em] text-lime-500 dark:text-lime-300">
                      {formUiText.eyebrow}
                    </p>
                    <h3 className="mt-2 text-2xl font-black tracking-tight text-gray-900 dark:text-white sm:text-3xl">
                      {t("contact.form.title", null, "Yhteydenottolomake")}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-blue-100/70">
                      {formUiText.hint}
                    </p>
                  </div>

                  <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-lime-400/25 bg-lime-400/10 text-lime-500 dark:text-lime-300">
                    <span className="contact-form-icon-pulse absolute inset-0 rounded-2xl border border-lime-400/25" />
                    <svg viewBox="0 0 24 24" className="relative h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                      <path d="M4 6.75A2.75 2.75 0 0 1 6.75 4h10.5A2.75 2.75 0 0 1 20 6.75v10.5A2.75 2.75 0 0 1 17.25 20H6.75A2.75 2.75 0 0 1 4 17.25V6.75Z" />
                      <path d="m5 7 6.05 4.54a1.6 1.6 0 0 0 1.9 0L19 7" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>

                {/* Virheet pysyvät lomakkeessa. Onnistunut lähetys avaa oman modalin. */}
                {status.type === "error" && (
                  <div className="contact-error mb-5 flex items-start gap-3 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-gray-800 dark:text-gray-100" role="alert">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/15 text-red-600 dark:text-red-300">!</span>
                    <span>{status.msg}</span>
                  </div>
                )}

                <form ref={form} onSubmit={sendEmail} onFocus={markFormStarted} className="space-y-4">
                  <input type="hidden" name="page_path" value={window.location?.pathname || ""} />
                  <input type="hidden" name="page_title" value={document.title || ""} />
                  <input type="hidden" name="lang" value={lang} />

                  <div className="contact-field rounded-2xl border border-gray-200 bg-gray-50 px-2 py-3 transition duration-300 dark:border-white/10 dark:bg-black/20 sm:p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="contact-field-index flex h-6 w-6 items-center justify-center rounded-lg bg-black text-[10px] font-black text-lime-300 dark:bg-white/10">01</span>
                      <label htmlFor="contact-user-name" className="text-sm font-bold text-gray-700 dark:text-white">{t("contact.form.fields.name.label", null, "Nimi")}</label>
                    </div>
                    <div className="relative">
                      <span className="contact-field-icon contact-field-icon-center pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-blue-200/60">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3.25" /><path d="M5.5 19c.65-3.25 3.07-5 6.5-5s5.85 1.75 6.5 5" strokeLinecap="round" /></svg>
                      </span>
                      <input id="contact-user-name" type="text" name="user_name" placeholder={t("contact.form.fields.name.placeholder", null, "Nimesi")} autoComplete="name" required onChange={markFormStarted} className="contact-field-control w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 pl-12 text-gray-900 outline-none transition duration-300 placeholder:text-gray-400 focus:border-lime-400 focus:ring-4 focus:ring-lime-400/10 dark:border-white/10 dark:bg-black/30 dark:text-white dark:placeholder:text-blue-100/40" />
                      <span className="contact-field-line pointer-events-none absolute inset-x-4 bottom-0 h-px origin-left scale-x-0 bg-gradient-to-r from-lime-400 via-sky-400 to-transparent transition-transform duration-500" />
                    </div>
                  </div>

                  <div className="contact-field rounded-2xl border border-gray-200 bg-gray-50 px-2 py-3 transition duration-300 dark:border-white/10 dark:bg-black/20 sm:p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="contact-field-index flex h-6 w-6 items-center justify-center rounded-lg bg-black text-[10px] font-black text-lime-300 dark:bg-white/10">02</span>
                      <label htmlFor="contact-user-email" className="text-sm font-bold text-gray-700 dark:text-white">{t("contact.form.fields.email.label", null, "Sähköposti")}</label>
                    </div>
                    <div className="relative">
                      <span className="contact-field-icon contact-field-icon-center pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-blue-200/60">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><rect x="3.5" y="5.5" width="17" height="13" rx="2.5" /><path d="m5 7 6.05 4.45a1.6 1.6 0 0 0 1.9 0L19 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </span>
                      <input id="contact-user-email" type="email" name="user_email" placeholder={t("contact.form.fields.email.placeholder", null, "sahkoposti@esimerkki.fi")} autoComplete="email" required onChange={markFormStarted} className="contact-field-control w-full rounded-xl border border-gray-200 bg-white px-4 py-3.5 pl-12 text-gray-900 outline-none transition duration-300 placeholder:text-gray-400 focus:border-lime-400 focus:ring-4 focus:ring-lime-400/10 dark:border-white/10 dark:bg-black/30 dark:text-white dark:placeholder:text-blue-100/40" />
                      <span className="contact-field-line pointer-events-none absolute inset-x-4 bottom-0 h-px origin-left scale-x-0 bg-gradient-to-r from-lime-400 via-sky-400 to-transparent transition-transform duration-500" />
                    </div>
                  </div>

                  <div className="contact-field rounded-2xl border border-gray-200 bg-gray-50 px-2 py-3 transition duration-300 dark:border-white/10 dark:bg-black/20 sm:p-3">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="contact-field-index flex h-6 w-6 items-center justify-center rounded-lg bg-black text-[10px] font-black text-lime-300 dark:bg-white/10">03</span>
                      <label htmlFor="contact-message" className="text-sm font-bold text-gray-700 dark:text-white">{t("contact.form.fields.message.label", null, "Viesti")}</label>
                    </div>
                    <div className="relative">
                      <span className="contact-field-icon pointer-events-none absolute left-4 top-4 text-gray-400 dark:text-blue-200/60">
                        <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5.75 4.5h12.5A2.75 2.75 0 0 1 21 7.25v7.5a2.75 2.75 0 0 1-2.75 2.75H11l-4.8 2.25.55-2.25h-1A2.75 2.75 0 0 1 3 14.75v-7.5A2.75 2.75 0 0 1 5.75 4.5Z" strokeLinejoin="round" /><path d="M7.5 9h9M7.5 12.5h6" strokeLinecap="round" /></svg>
                      </span>
                      <textarea id="contact-message" name="message" placeholder={t("contact.form.fields.message.placeholder", null, "Kerro projektista: mitä, miksi, milloin?")} rows={5} required onChange={markFormStarted} className="contact-field-control min-h-[132px] w-full resize-none rounded-xl border border-gray-200 bg-white px-4 py-3.5 pl-12 text-gray-900 outline-none transition duration-300 placeholder:text-gray-400 focus:border-lime-400 focus:ring-4 focus:ring-lime-400/10 dark:border-white/10 dark:bg-black/30 dark:text-white dark:placeholder:text-blue-100/40" />
                      <span className="contact-field-line pointer-events-none absolute inset-x-4 bottom-0 h-px origin-left scale-x-0 bg-gradient-to-r from-lime-400 via-sky-400 to-transparent transition-transform duration-500" />
                    </div>
                  </div>

                  <div className={"contact-captcha rounded-2xl border px-2 py-4 transition duration-300 sm:p-4 " + (captchaVerified ? "border-lime-400/40 bg-lime-400/10" : "border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-black/20")}>
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-white">
                        <span className={"flex h-8 w-8 items-center justify-center rounded-xl transition duration-300 " + (captchaVerified ? "bg-lime-400/15 text-lime-600 dark:text-lime-300" : "bg-gray-200 text-gray-500 dark:bg-white/10 dark:text-blue-100/60")}>
                          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3.5 19 6v5.3c0 4.35-2.72 7.67-7 9.2-4.28-1.53-7-4.85-7-9.2V6l7-2.5Z" strokeLinejoin="round" /><path d="m8.8 12 2.1 2.1 4.4-4.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        </span>
                        <span>{formUiText.secureTitle}</span>
                      </div>
                      <span className={"rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] transition duration-300 " + (captchaVerified ? "bg-lime-400/15 text-lime-700 dark:text-lime-300" : "bg-gray-200 text-gray-500 dark:bg-white/10 dark:text-blue-100/60")}>{captchaVerified ? formUiText.secureVerified : formUiText.securePending}</span>
                    </div>

                    <div ref={recaptchaWrapRef} className="w-full max-w-full overflow-hidden rounded-xl" style={{ minHeight: `${RECAPTCHA_HEIGHT * recaptchaScale}px` }}>
                      <div style={{ width: `${RECAPTCHA_WIDTH}px`, height: `${RECAPTCHA_HEIGHT}px`, transform: `scale(${recaptchaScale})`, transformOrigin: "left top" }}>
                        <ReCAPTCHA ref={recaptchaRef} key={lang} hl={lang} sitekey="6LcyYNUqAAAAAKi0klY8JSMv_aZwvnxyCDUin81_" onChange={onCaptchaChange} />
                      </div>
                    </div>

                    <p className="mt-2 text-xs leading-relaxed text-gray-500 dark:text-blue-100/60">{t("contact.form.captchaNote", null, "CAPTCHA estää roskapostia. Ihmiset pääsee läpi, botit jää parkkiin.")}</p>
                  </div>

                  <div className="pt-1">
                    <ThemedButton
                      type="submit"
                      disabled={isSending}
                      onClick={() => {
                        fireEvent("contact_submit_click", {
                          captcha_ok: captchaVerified,
                          page_path: window.location?.pathname || "",
                          lang,
                        });
                      }}
                      className={"contact-submit w-full !rounded-xl !py-3.5 !text-base !font-black transition duration-300 " + (isSending ? "cursor-not-allowed opacity-70" : "hover:-translate-y-0.5")}
                    >
                      <span className="flex items-center justify-center gap-2.5">
                        {isSending ? (
                          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" aria-hidden="true" />
                        ) : (
                          <svg viewBox="0 0 24 24" className="contact-submit-arrow h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m4 12 16-8-5.5 16-3.2-6.3L4 12Z" strokeLinejoin="round" /><path d="m11.3 13.7 4.4-4.4" strokeLinecap="round" /></svg>
                        )}
                        <span>{isSending ? t("contact.form.submit.sending", null, "Lähetetään...") : t("contact.form.submit.default", null, "Lähetä viesti")}</span>
                      </span>
                    </ThemedButton>

                    <div className="mt-3 flex items-start justify-center gap-2 text-center text-xs leading-relaxed text-gray-500 dark:text-blue-100/60">
                      <svg viewBox="0 0 24 24" className="mt-0.5 h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M8 10V8a4 4 0 1 1 8 0v2" strokeLinecap="round" /><rect x="5" y="10" width="14" height="10" rx="2" /></svg>
                      <span>{formUiText.privacy}</span>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/*
        Onnistuneen lähetyksen modal.
        Ei ulkoista animaatiokirjastoa: kaikki animaatiot ovat tässä komponentissa,
        joten nykyiseen webpack/CRA/Tailwind-konfiguraatioon ei tarvitse koskea.
      */}
      {showSuccessModal && (
        <div
          className="fixed inset-0 z-[60000] flex items-center justify-center px-4 py-6 sm:px-6"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeSuccessModal();
          }}
          style={{
            animation: successModalClosing
              ? "contactBackdropOut 220ms ease-in both"
              : "contactBackdropIn 260ms ease-out both",
          }}
        >
          {/* Tumma blur-tausta + kevyt brändihehku */}
          <div className="pointer-events-none absolute inset-0 bg-slate-950/75 backdrop-blur-md" />
          <div className="pointer-events-none absolute left-1/2 top-1/2 h-[34rem] w-[34rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-lime-400/10 blur-3xl" />
          <div className="pointer-events-none absolute left-[28%] top-[28%] h-56 w-56 rounded-full bg-sky-400/15 blur-3xl" />

          <div
            className="relative w-full max-w-lg overflow-hidden rounded-[2rem] border border-white/15 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 p-6 text-white shadow-[0_35px_120px_rgba(0,0,0,0.65)] sm:p-8"
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-success-title"
            aria-describedby="contact-success-description"
            onMouseDown={(event) => event.stopPropagation()}
            style={{
              animation: successModalClosing
                ? "contactModalOut 220ms ease-in both"
                : "contactModalIn 520ms cubic-bezier(0.16,1,0.3,1) both",
            }}
          >
            {/* Cardin sisäiset valot */}
            <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-lime-400/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-sky-500/20 blur-3xl" />
            <div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-lime-300/80 to-transparent" />

            {/* Sulje-rasti */}
            <button
              ref={successModalCloseRef}
              type="button"
              onClick={closeSuccessModal}
              className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/70 transition duration-200 hover:rotate-90 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-lime-400/70"
              aria-label={t(
                "contact.form.successModal.closeAria",
                null,
                successModalText.closeAria
              )}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
              </svg>
            </button>

            <div className="relative z-10 text-center">
              {/* Animoitu onnistumisikoni */}
              <div className="relative mx-auto mb-6 flex h-24 w-24 items-center justify-center">
                <div className="absolute inset-0 rounded-full bg-lime-400/20 blur-xl animate-pulse" />
                <div className="absolute inset-1 rounded-full border border-lime-300/30" />
                <div className="absolute inset-3 rounded-full border border-lime-300/20 animate-ping" />

                {/* Pienet kipinät */}
                <span className="absolute -right-1 top-2 h-2.5 w-2.5 rounded-full bg-sky-300 contact-success-spark contact-success-spark-1" />
                <span className="absolute -left-2 top-8 h-2 w-2 rotate-45 bg-lime-300 contact-success-spark contact-success-spark-2" />
                <span className="absolute bottom-1 right-4 h-1.5 w-1.5 rounded-full bg-white contact-success-spark contact-success-spark-3" />

                <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-lime-400 text-slate-950 shadow-[0_0_45px_rgba(163,230,53,0.45)]">
                  <svg
                    viewBox="0 0 52 52"
                    className="h-9 w-9"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      className="contact-success-check"
                      d="M14 27.5 22.5 36 39 18"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <p className="text-xs font-black tracking-[0.28em] text-lime-300 sm:text-sm">
                {t(
                  "contact.form.successModal.eyebrow",
                  null,
                  successModalText.eyebrow
                )}
              </p>

              <h3
                id="contact-success-title"
                className="mt-3 text-3xl font-black tracking-tight text-white sm:text-4xl"
              >
                {t(
                  "contact.form.successModal.title",
                  null,
                  successModalText.title
                )}
              </h3>

              <p
                id="contact-success-description"
                className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-300 sm:text-base"
              >
                {t(
                  "contact.form.successModal.body",
                  null,
                  successModalText.body
                )}
              </p>

              {/* Kevyt “mitä nyt tapahtuu” -rivi */}
              <div className="mt-7 grid gap-3 text-left sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-lime-400/15 text-sm font-black text-lime-300">
                      1
                    </span>
                    <span className="text-sm font-semibold text-slate-100">
                      {t(
                        "contact.form.successModal.first",
                        null,
                        successModalText.first
                      )}
                    </span>
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-400/15 text-sm font-black text-sky-300">
                      2
                    </span>
                    <span className="text-sm font-semibold text-slate-100">
                      {t(
                        "contact.form.successModal.second",
                        null,
                        successModalText.second
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={closeSuccessModal}
                className="group mt-7 flex w-full items-center justify-center gap-2 rounded-2xl bg-lime-400 px-5 py-3.5 text-base font-black text-slate-950 shadow-[0_18px_45px_rgba(163,230,53,0.22)] transition duration-200 hover:-translate-y-0.5 hover:bg-lime-300 hover:shadow-[0_22px_55px_rgba(163,230,53,0.30)] focus:outline-none focus:ring-2 focus:ring-lime-300 focus:ring-offset-2 focus:ring-offset-slate-950"
              >
                <span>
                  {t(
                    "contact.form.successModal.close",
                    null,
                    successModalText.close
                  )}
                </span>
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden="true"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Kontaktiosion omat keyframet: ei tarvitse muuttaa Tailwind-konfiguraatiota. */}
      <style>{`
        @keyframes contactFormRise {
          0% { opacity: 0; transform: translateY(22px) scale(0.985); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes contactFormSheen {
          0%, 58% { transform: translateX(-180%) rotate(12deg); opacity: 0; }
          66% { opacity: 0.9; }
          82% { transform: translateX(520%) rotate(12deg); opacity: 0; }
          100% { transform: translateX(520%) rotate(12deg); opacity: 0; }
        }

        @keyframes contactIconPulse {
          0%, 100% { transform: scale(1); opacity: 0.28; }
          50% { transform: scale(1.18); opacity: 0; }
        }

        @keyframes contactErrorIn {
          0% { opacity: 0; transform: translateY(-8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        .contact-form-shell {
          animation: contactFormRise 650ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .contact-form-sheen {
          animation: contactFormSheen 6.5s ease-in-out 1.2s infinite;
        }

        .contact-form-icon-pulse {
          animation: contactIconPulse 2.6s ease-in-out infinite;
        }

        .contact-field {
          position: relative;
        }

        .contact-field:focus-within {
          transform: translateY(-2px);
          border-color: rgba(163, 230, 53, 0.45);
          box-shadow: 0 14px 36px rgba(15, 23, 42, 0.08), 0 0 0 1px rgba(163, 230, 53, 0.08);
        }

        .contact-field-icon,
        .contact-field-index {
          transition: transform 260ms ease, color 260ms ease, background-color 260ms ease;
        }

        .contact-field:focus-within .contact-field-icon-center {
          color: rgb(132 204 22);
          transform: translateY(-50%) scale(1.12);
        }

        .contact-field:focus-within .contact-field-icon:not(.contact-field-icon-center) {
          color: rgb(132 204 22);
          transform: scale(1.12);
        }

        .contact-field:focus-within .contact-field-line {
          transform: scaleX(1);
        }

        .contact-field:focus-within .contact-field-index {
          background: rgb(132 204 22);
          color: rgb(15 23 42);
          transform: rotate(-4deg) scale(1.05);
        }

        .contact-error {
          animation: contactErrorIn 320ms cubic-bezier(0.16, 1, 0.3, 1) both;
        }

        .contact-submit-arrow {
          transition: transform 220ms ease;
        }

        .contact-submit:hover .contact-submit-arrow {
          transform: translate(3px, -2px) rotate(-4deg);
        }

        @keyframes contactBackdropIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes contactBackdropOut {
          from { opacity: 1; }
          to { opacity: 0; }
        }

        @keyframes contactModalIn {
          0% {
            opacity: 0;
            transform: translateY(28px) scale(0.92);
            filter: blur(8px);
          }
          70% {
            transform: translateY(-4px) scale(1.015);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }

        @keyframes contactModalOut {
          from {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          to {
            opacity: 0;
            transform: translateY(12px) scale(0.96);
          }
        }

        @keyframes contactCheckDraw {
          from { stroke-dashoffset: 42; }
          to { stroke-dashoffset: 0; }
        }

        @keyframes contactSpark {
          0% { opacity: 0; transform: scale(0.3) translateY(6px); }
          45% { opacity: 1; transform: scale(1.25) translateY(-3px); }
          100% { opacity: 0.85; transform: scale(1) translateY(0); }
        }

        .contact-success-check {
          stroke-dasharray: 42;
          stroke-dashoffset: 42;
          animation: contactCheckDraw 620ms 280ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .contact-success-spark {
          opacity: 0;
          animation: contactSpark 520ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .contact-success-spark-1 { animation-delay: 340ms; }
        .contact-success-spark-2 { animation-delay: 430ms; }
        .contact-success-spark-3 { animation-delay: 520ms; }

        @media (prefers-reduced-motion: reduce) {
          .contact-form-shell,
          .contact-form-sheen,
          .contact-form-icon-pulse,
          .contact-error,
          .contact-success-check,
          .contact-success-spark {
            animation: none !important;
            opacity: 1;
            stroke-dashoffset: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;